import { Tab, Tabs, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import IBANJSValidator from 'iban';
import React, {
	ChangeEvent,
	FunctionComponent,
	useEffect,
	useState,
} from 'react';
import {useTranslation, WithTranslation} from 'react-i18next';
import NavigationBar from '../../../components/NavigationBar';
import { PageTitle } from '../../../components/PageTitle/PageTitle';
import { useUniversalTracking } from '../../../lib/tracking/universalTracking';
import AccountNumberBLZ from '../../../pages/InsurancePages/Payment/components/AccountNumberBLZ/AccountNumberBLZ';
import { BankValidationState, ContractState } from '../../../types/redux';
import { formatIban } from '../../../utils/formatString';
import BankInfo from './components/BankInfo/BankInfo';
import IBAN from './components/IBAN/IBAN';

const useStyles = makeStyles((theme: Theme) => ({
	tabRoot: {
		'&$tabSelected': {
			color: theme.palette.primary.main,
		},
	},
	paymentTabInput: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-evenly',
		border: '1px solid rgba(0, 0, 0, 0.13)',
		padding: theme.spacing(2),
		[theme.breakpoints.up('sm')]: {
			marginRight: '24px',
			width: '450px',
			padding: theme.spacing(8),
		},
	},
	paymentTab: {
		display: 'flex',
		flexDirection: 'column',
		[theme.breakpoints.up('sm')]: {
			flexDirection: 'row',
		},
	},
	tabSelected: {},
	tabsIndicator: {
		backgroundColor: theme.palette.primary.main,
	},
	tabsRoot: {
		fontSize: '14px',
		lineHeight: '18px',
	},
}));

interface Props extends WithTranslation, StateProps, DispatchProps {
	onSubmit(data: any): void;
	validateIBAN(iban: string): void;
	cancelValidation(): void;
}

const IBAN_TAB = 0;
const ACCOUNT_NUMBER_TAB = 1;

const Payment: FunctionComponent<Props> = (props) => {
	const { t } = useTranslation();
	const { validateIBAN, cancelValidation } = props;
	const [tab, setTab] = useState(IBAN_TAB);
	const [IBANNumber, setIBANNumber] = useState('');
	const [accountNumber, setAccountNumber] = useState('');
	const [blz, setBlz] = useState('');

	const [IBANNumberError, setIBANNumberError] = useState('');
	const { trackEvent } = useUniversalTracking();
	const classes = useStyles();

	function validateTab(): void {
		let ibannumber = '';
		switch (tab) {
			case IBAN_TAB:
				ibannumber = IBANNumber;
				break;
			case ACCOUNT_NUMBER_TAB:
				const bankaccount = blz + accountNumber;
				if (bankaccount.length === 18) {
					ibannumber = IBANJSValidator.fromBBAN('DE', blz + accountNumber);
				}
		}
		if (/^DE(.*)|^\s*$/i.test(ibannumber) && IBANJSValidator.isValid(ibannumber)) {
				validateIBAN(ibannumber);
		} else {
				cancelValidation();
		}
	}

	useEffect( validateTab, [IBANNumber, accountNumber, blz, tab, cancelValidation, validateIBAN]);

	useEffect(
		() => {
			setIBANNumber(props.contract.iban)
		},
		// eslint-disable-next-line
		[]
	);

	useEffect(
		() => {
			if (IBANNumber.length > 0 ) {
				if (IBANNumber.length > 1  && !/^$|^DE(.*)|^\s*$/i.test(IBANNumber)) {
					setIBANNumberError(t('payment.wrong_country'))
				} else
					setIBANNumberError(props.bankValidation.isValid === false ?  props.t('payment.wrong_IBAN' as const) : '');
			}
		},
		// eslint-disable-next-line
		[props.bankValidation.isValid, IBANNumber]
	);
	function handleTabChange(event: ChangeEvent<{}>, newValue: number): void {
		cancelValidation();
		setTab(newValue);
		trackEvent({
			category: 'payment-switch-tab',
			action: 'click-event',
			label: 'payment',
			value: newValue,
		});
	}
	function a11yProps(index: any): {} {
		return {
			id: `simple-tab-${index}`,
			'aria-controls': `simple-tabpanel-${index}`,
		};
	}

	const validateAndSubmit = () => {
		if (props.bankValidation.IBAN === '**') {
			setIBANNumberError(props.t('payment.wrong_IBAN' as const));
		}

		if (props.bankValidation.isValid) {
			onSubmit();
		}
	};

	const onSubmit = () => {
		trackEvent({
			category: 'payment-submit',
			action: 'click-event',
			label: 'payment',
		});
		props.onSubmit({
			iban: props.bankValidation.IBAN,
			bic: props.bankValidation.BIC,
			bank: props.bankValidation.bankName,
		});
	};

	return (
		<div className="payment_main">
			<PageTitle title={props.t('payment.title')} />
			<Tabs
				value={tab}
				onChange={handleTabChange}
				indicatorColor="primary"
				textColor="primary"
				classes={{
					root: classes.tabsRoot,
					indicator: classes.tabsIndicator,
				}}
			>
				<Tab
					{...a11yProps(0)}
					label={props.t('payment.IBAN_tab')}
					classes={{
						root: classes.tabRoot,
						selected: classes.tabSelected,
					}}
				/>
				<Tab
					label={props.t('payment.account_number_tab')}
					{...a11yProps(1)}
					classes={{
						root: classes.tabRoot,
						selected: classes.tabSelected,
					}}
				/>
			</Tabs>
			<div className={classes.paymentTab}>
				<div className={classes.paymentTabInput}>
					{tab === IBAN_TAB && (
						<IBAN
							IBAN={formatIban(IBANNumber)}
							setIBAN={setIBANNumber}
							errorMessage={IBANNumberError}
						/>
					)}
					{tab === ACCOUNT_NUMBER_TAB && (
						<AccountNumberBLZ
							account={accountNumber}
							setAccount={setAccountNumber}
							isValid={props.bankValidation.isValid}
							blz={blz}
							setBlz={setBlz}
						/>
					)}
				</div>
				<div style={{ backgroundColor: '#e7e7e7' }}>
					<BankInfo
						IBAN={formatIban(props.bankValidation.IBAN)}
						BIC={props.bankValidation.BIC}
						Bank={props.bankValidation.bankName}
					/>
				</div>
			</div>
			<NavigationBar onClickContinue={validateAndSubmit} />
		</div>
	);
};

interface StateProps {
	contract: ContractState;
	bankValidation: BankValidationState;
}

interface DispatchProps {}

export default Payment;
